import React, {useEffect, useState} from "react";

/**
 * Floating "back to top" button. Appears once the page is scrolled past ~60% of
 * the viewport and smooth-scrolls to the top on click (respecting reduced motion).
 *
 * Optional props:
 *   raised        - lift the button above a fixed bottom bar (e.g. the podcast player)
 *   avoidSelector - CSS selector of that bottom bar; its live height is measured so
 *                   the button always clears it, on desktop and mobile alike.
 */
const BackToTop = ({raised = false, avoidSelector}) => {
    const [visible, setVisible] = useState(false);
    const [avoidH, setAvoidH] = useState(0);

    // Toggle visibility on scroll (rAF-throttled).
    useEffect(() => {
        let ticking = false;
        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                setVisible(window.scrollY > window.innerHeight * 0.6);
                ticking = false;
            });
        };
        window.addEventListener("scroll", onScroll, {passive: true});
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // When raised, track the avoided bar's height so we always clear it.
    useEffect(() => {
        if (!raised || !avoidSelector) {
            setAvoidH(0);
            return;
        }
        const el = document.querySelector(avoidSelector);
        if (!el) return;
        const update = () => setAvoidH(el.offsetHeight || 0);
        update();
        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(update);
            ro.observe(el);
        }
        window.addEventListener("resize", update);
        return () => {
            if (ro) ro.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [raised, avoidSelector]);

    const toTop = () => {
        const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        window.scrollTo({top: 0, behavior: reduce ? "auto" : "smooth"});
    };

    const style = avoidH ? {bottom: `calc(${avoidH}px + 20px + env(safe-area-inset-bottom, 0px))`} : undefined;

    return (
        <button
            type="button"
            className={`BackToTop${visible ? " BackToTop--visible" : ""}`}
            style={style}
            onClick={toTop}
            aria-label="Back to top"
            aria-hidden={visible ? undefined : "true"}
            tabIndex={visible ? 0 : -1}
        >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 19V6" />
                <path d="M6 12l6-6 6 6" />
            </svg>
        </button>
    );
};

export default BackToTop;
